/**
 * Play a short UI sound from a URL.
 * Creates a fresh Audio element inside the caller’s user-gesture turn so autoplay
 * policies allow it, and keeps a module ref so React unmounts don’t cut it off.
 */

let activeSound = null

export const DEFAULT_COMPLETE_SOUND = `/sounds/fanfare.mp3`
export const DEFAULT_SUCCESS_SOUND = `/sounds/ding.mp3`
export const DEFAULT_SOFT_FAIL_SOUND = `/sounds/duck.mp3`
export const DEFAULT_FAIL_SOUND = `/sounds/sad_trombone.mp3`

export const huntSoundsEnabled = hunt => {
  if (!hunt) {
    return true
  }
  const raw = hunt.play_sounds
  // Default on; only an explicit “No” (0) disables.
  return raw !== 0 && raw !== `0` && raw !== false
}

/** Prefer bundled app sounds; hunt overrides on games.watson are often gzip-broken for Audio(). */
export const resolveHuntSound = (overrideUrl, fallback) => {
  if (typeof overrideUrl === `string` && overrideUrl.trim()) {
    const url = overrideUrl.trim()
    // LiteSpeed gzip + Content-Type audio/mpeg breaks Chrome Audio on the API host.
    if (/games\.watsonadventures\.com/i.test(url)) {
      return fallback
    }
    return url
  }
  return fallback
}

export const playSound = src => {
  if (typeof window === `undefined`) {
    return Promise.resolve()
  }
  const url = typeof src === `string` ? src.trim() : ``
  if (!url) {
    // eslint-disable-next-line no-console
    console.warn(`Sound play skipped: empty src`)
    return Promise.resolve()
  }
  try {
    if (activeSound) {
      activeSound.pause()
    }
  } catch (e) {
    // ignore
  }
  const audio = new Audio(url)
  audio.preload = `auto`
  activeSound = audio
  const playPromise = audio.play()
  if (playPromise && typeof playPromise.then === `function`) {
    return playPromise.catch(err => {
      // eslint-disable-next-line no-console
      console.warn(`Sound play failed:`, url, err)
      throw err
    })
  }
  return Promise.resolve()
}

export const playHuntCompleteSound = hunt => {
  if (!huntSoundsEnabled(hunt)) {
    // eslint-disable-next-line no-console
    console.warn(`Finish sound skipped: hunt play_sounds is off`)
    return Promise.resolve()
  }
  const src = resolveHuntSound(hunt && hunt.sound_complete && hunt.sound_complete.file, DEFAULT_COMPLETE_SOUND)
  try {
    sessionStorage.setItem(`wa_fanfare_pending`, `1`)
  } catch (e) {
    // ignore
  }
  return playSound(src).then(
    () => {
      try {
        sessionStorage.removeItem(`wa_fanfare_pending`)
      } catch (e) {
        // ignore
      }
    },
    () => {
      // Keep wa_fanfare_pending so Score card click can retry.
    }
  )
}

/** Fallback when autoplay from finish submit was blocked — call from a later click. */
export const playPendingHuntCompleteSound = () => {
  try {
    if (sessionStorage.getItem(`wa_fanfare_pending`) !== `1`) {
      return Promise.resolve()
    }
    sessionStorage.removeItem(`wa_fanfare_pending`)
  } catch (e) {
    return Promise.resolve()
  }
  return playSound(DEFAULT_COMPLETE_SOUND)
}
